import { useCallback, useEffect, useRef, useState } from 'react';
import { createConsumer, type Subscription } from '@rails/actioncable';
import { captureException } from '@sentry/react';
import { getAuthToken } from '@/utils/auth-token';

export enum FeedbackSkillsStreamingEvent {
  Started = 'started',
  TextChunk = 'text_chunk',
  Skills = 'skills',
  Completed = 'completed',
  StreamEnded = 'stream_ended',
  Error = 'error',
}

export interface StreamedSkill {
  id: number;
  name_pt: string;
  name_en: string;
  score: number;
  sequence: number;
}

interface UseFeedbackSkillsStreamingOptions {
  onCompleted?: (summary: string, skills: StreamedSkill[]) => void;
  onError?: (message: string) => void;
}

interface ReceivedData extends Record<string, unknown> {
  event: FeedbackSkillsStreamingEvent;
}

export const useFeedbackSkillsStreaming = ({
  onCompleted,
  onError,
}: UseFeedbackSkillsStreamingOptions = {}) => {
  const [status, setStatus] = useState<FeedbackSkillsStreamingEvent>(
    FeedbackSkillsStreamingEvent.Started
  );
  const [summary, setSummary] = useState('');
  const [skills, setSkills] = useState<StreamedSkill[]>([]);

  const subscriptionRef = useRef<Subscription | null>(null);
  const consumerRef = useRef<ReturnType<typeof createConsumer> | null>(null);
  const summaryRef = useRef('');
  const skillsRef = useRef<StreamedSkill[]>([]);
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);

  onCompletedRef.current = onCompleted;
  onErrorRef.current = onError;

  const disconnect = useCallback(() => {
    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = null;
    consumerRef.current?.disconnect();
    consumerRef.current = null;
  }, []);

  const startStreaming = useCallback(
    (feedbackId: number) => {
      disconnect();

      summaryRef.current = '';
      skillsRef.current = [];
      setSummary('');
      setSkills([]);
      setStatus(FeedbackSkillsStreamingEvent.Started);

      const token = getAuthToken();
      const sessionId = `guidance_feedback_${feedbackId}_${Date.now()}`;

      consumerRef.current = createConsumer(`/cable?token=${token}`);
      subscriptionRef.current = consumerRef.current.subscriptions.create(
        {
          channel: 'Guidance::Channel::FeedbackSkillsInference',
          session_id: sessionId,
        },
        {
          connected() {
            subscriptionRef.current?.perform('generate', {
              feedback_id: feedbackId,
            });
          },
          received(data: ReceivedData) {
            const eventHandlers: Record<FeedbackSkillsStreamingEvent, () => void> = {
              started: () => setStatus(FeedbackSkillsStreamingEvent.TextChunk),
              text_chunk: () => {
                if (data.content) {
                  summaryRef.current += data.content;
                  setSummary(summaryRef.current);
                }
              },
              skills: () => {
                if (data.skills) {
                  skillsRef.current = data.skills as StreamedSkill[];
                  setSkills(skillsRef.current);
                }
              },
              completed: () => {
                setStatus(FeedbackSkillsStreamingEvent.Completed);
              },
              // The backend only broadcasts "completed" after persisting the
              // recommendation, but we still wait for "stream_ended" (broadcast
              // right after) to notify the caller, since that's the definitive
              // end-of-stream signal the channel emits.
              stream_ended: () => {
                disconnect();
                onCompletedRef.current?.(summaryRef.current, skillsRef.current);
              },
              error: () => {
                const message =
                  typeof data.message === 'string'
                    ? data.message
                    : 'Unknown error';
                setStatus(FeedbackSkillsStreamingEvent.Error);
                disconnect();
                onErrorRef.current?.(message);
                captureException(
                  new Error(`Guidance feedback skills streaming error: ${message}`)
                );
              },
            };

            eventHandlers[data.event]?.();
          },
          rejected() {
            setStatus(FeedbackSkillsStreamingEvent.Error);
            onErrorRef.current?.('Connection rejected');
          },
        }
      );
    },
    [disconnect]
  );

  useEffect(() => disconnect, [disconnect]);

  return {
    status,
    summary,
    skills,
    startStreaming,
  };
};
